import axios from "axios"
import dayjs from "../utils/dayjs"
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { setCounter } from "../app/slices/counterSlice"
import { store } from "../app/store"
import { getParamFromQueryString, isValidToken } from "../utils/utils"
import ConfigContext from "./ConfigProvider"

interface IAuthContext {
  isLoggedIn: boolean
  logoutHandler: () => void
  logout: () => void
  georgeExchange: () => void
  refresh: () => void
}

interface IReturnState {
  redirectUri?: string
  returnState?: string
}

interface AuthProviderProps {
  children: ReactNode
}

const AuthContext = React.createContext<IAuthContext>({} as IAuthContext)

const GEORGE_CLIENT_APP = "gapp"
const GEORGE_CLIENT_WEB = "gweb"
const GEORGE_CLIENT_IDS = new Set([GEORGE_CLIENT_APP, GEORGE_CLIENT_WEB])
const GEORGE_EXPIRY = "300"

export const isFromGeorge = (clientId?: string): boolean => {
  const origin = clientId || sessionStorage.getItem("origin")
  return !!origin && GEORGE_CLIENT_IDS.has(origin)
}

export const isFromGeorgeWeb = (): boolean => {
  const origin = sessionStorage.getItem("origin")
  return !!origin && GEORGE_CLIENT_WEB === origin
}

export const isFromGeorgeApp = (): boolean => {
  const origin = sessionStorage.getItem("origin")
  return !!origin && GEORGE_CLIENT_APP === origin
}

export const getGeorgeRedirectUri = (): IReturnState | undefined => {
  if (!isFromGeorge()) {
    return undefined
  }

  const storedState = sessionStorage.getItem("state")
  if (!storedState) {
    return undefined
  }

  const parsedState = parseState(storedState)
  if (!parsedState?.redirectUri) {
    return undefined
  }

  return parsedState
}

const parseState = (encodedState: string): IReturnState | undefined => {
  try {
    return JSON.parse(atob(encodedState))
  } catch {
    return undefined
  }
}

export const AuthProvider = (props: AuthProviderProps) => {
  const storedToken = isValidToken()
  const [isLoggedIn, setIsLoggedIn] = useState(storedToken)
  const configContext = useContext(ConfigContext)
  const initialized = useRef(false)
  const isLoggingOut = useRef(false)

  if (!isValidToken()) {
    sessionStorage.clear()
  }

  const logout = useCallback(() => {
    if (isLoggingOut.current) {
      return
    }
    isLoggingOut.current = true

    const georgeRedirect = getGeorgeRedirectUri()

    sessionStorage.clear()

    globalThis.location.href = georgeRedirect?.redirectUri
      ? georgeRedirect.redirectUri
      : `${configContext?.oAuth?.url}/authorise?response_type=code&redirect_uri=${configContext?.oAuth?.redirectUrl}&scope=${configContext?.oAuth?.tokenScope}&client_id=${configContext?.oAuth?.clientId}`
  }, [configContext?.oAuth])

  const georgeExchange = useCallback(async () => {
    const georgeRedirect = getGeorgeRedirectUri()
    if (!georgeRedirect?.redirectUri) {
      logout()
      return
    }

    const { redirectUri, returnState } = georgeRedirect
    const georgeUrl = new URL(redirectUri)

    const accessToken = sessionStorage.getItem("access_token")
    const origin = sessionStorage.getItem("origin")

    if (!georgeUrl || !accessToken || !origin) {
      logout()
      return
    }

    try {
      const exchangeUrl = new URL(configContext.exchangeUrl)
      exchangeUrl.searchParams.set("targetClientId", origin)
      exchangeUrl.searchParams.set("revoke", "true")

      const response = await axios.get(exchangeUrl.toString(), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      const newToken = response?.data?.["access_token"]
      const tokenType = response?.data?.["token_type"]
      const expiresIn = response?.data?.["expires_in"]
      const scope = response?.data?.["scope"]

      if (!!georgeUrl && !!newToken) {
        const hashParams = new URLSearchParams()
        hashParams.set("access_token", newToken)
        if (tokenType) hashParams.set("token_type", tokenType)
        if (expiresIn) hashParams.set("expires_in", String(expiresIn))
        if (scope) hashParams.set("scope", scope)
        if (returnState) hashParams.set("state", returnState)
        georgeUrl.hash = hashParams.toString()
      }
    } catch {
      logout()
      return
    }

    if (georgeUrl) {
      sessionStorage.clear()

      globalThis.location.href = georgeUrl.toString()
    } else {
      logout()
    }
  }, [configContext.exchangeUrl, logout])

  const logoutHandler = useCallback(() => {
    if (isFromGeorge()) {
      georgeExchange()
    } else {
      logout()
    }
  }, [georgeExchange, logout])

  useEffect(() => {
    if (initialized.current) {
      return
    }

    initialized.current = true

    const hashParams = new URLSearchParams(
      globalThis.location.hash?.substring(1) || "",
    )
    const gAccessToken = hashParams?.get("access_token")
    const origin = getParamFromQueryString("origin")

    if (!isLoggedIn && isFromGeorge(origin) && !!gAccessToken) {
      ;(async () => {
        const stateEncoded = getParamFromQueryString("state") ?? ""
        sessionStorage.setItem("origin", origin)
        sessionStorage.setItem("state", stateEncoded)

        try {
          const response = await axios.post(
            configContext.introspectUrl,
            { token: gAccessToken },
            {
              headers: {
                Authorization: `Bearer ${gAccessToken}`,
              },
            },
          )

          if (!response?.data?.["active"]) {
            throw new Error("Invalid token")
          }

          const expiresIn = response.data.exp || GEORGE_EXPIRY

          sessionStorage.clear()
          sessionStorage.setItem("access_token", gAccessToken)
          sessionStorage.setItem("created_at", dayjs().format())
          sessionStorage.setItem("expires_in", String(expiresIn))
          sessionStorage.setItem("state", stateEncoded)
          sessionStorage.setItem(
            "customer_id",
            getParamFromQueryString("customer_id") ?? "",
          )
          sessionStorage.setItem(
            "user_id",
            getParamFromQueryString("user_id") ?? "",
          )
          sessionStorage.setItem("origin", origin)

          setIsLoggedIn(true)
        } catch {
          logout()
        }
      })()

      return
    }

    const code = getParamFromQueryString("code")

    if (!isLoggedIn && !code)
      globalThis.location.href = `${configContext?.oAuth?.url}/authorise?response_type=code&redirect_uri=${configContext?.oAuth?.redirectUrl}&scope=${configContext?.oAuth?.tokenScope}&client_id=${configContext?.oAuth?.clientId}`

    if (code) {
      ;(async () => {
        const response = await axios.post(
          `${configContext?.oAuth?.url}/token?grant_type=authorization_code&code=${code}&redirect_uri=${configContext?.oAuth?.redirectUrl}&scope=${configContext?.oAuth?.tokenScope}&client_id=${configContext?.oAuth?.clientId}`,
          null,
        )

        sessionStorage.clear()
        sessionStorage.setItem("access_token", response.data.access_token)
        sessionStorage.setItem("refresh_token", response.data.refresh_token)
        sessionStorage.setItem("expires_in", response.data.expires_in)
        sessionStorage.setItem("created_at", dayjs().format())
        sessionStorage.setItem("origin", "mikrovt")

        setIsLoggedIn(!!response.data.access_token)
      })()
    }
  })

  const refreshGeorge = useCallback(async () => {
    const refreshToken = sessionStorage.getItem("access_token")

    try {
      const response = await axios.post(
        `${configContext?.slideUrl}`,
        { access_token: refreshToken },
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        },
      )

      if (response.status !== 200) {
        throw new Error("Refresh error")
      }

      sessionStorage.setItem("access_token", response.data.access_token)
      sessionStorage.setItem("expires_in", GEORGE_EXPIRY)
      sessionStorage.setItem("created_at", dayjs().format())
      store.dispatch(setCounter())
    } catch {
      // Token refresh failed
    }
  }, [configContext?.slideUrl])

  const refreshOauth = useCallback(async () => {
    const refreshToken = sessionStorage.getItem("refresh_token")
    const config = configContext?.oAuth

    const response = await axios.post(
      `${config?.url}/token?grant_type=refresh_token&refresh_token=${refreshToken}&redirect_uri=${config?.redirectUrl}&scope=${config?.tokenScope}&client_id=${config?.clientId}`,
      null,
    )

    sessionStorage.setItem("access_token", response.data.access_token)
    sessionStorage.setItem("expires_in", response.data.expires_in)
    sessionStorage.setItem("created_at", dayjs().format())

    store.dispatch(setCounter())
  }, [configContext?.oAuth])

  const refresh = useCallback(() => {
    if (isFromGeorge()) {
      refreshGeorge()
      return
    }

    refreshOauth()
  }, [refreshGeorge, refreshOauth])

  const context = useMemo(
    () => ({
      isLoggedIn,
      logout,
      georgeExchange,
      refresh,
      logoutHandler,
    }),
    [isLoggedIn, logout, georgeExchange, refresh, logoutHandler],
  )

  return context && isLoggedIn ? (
    <AuthContext.Provider value={context}>
      {props.children}
    </AuthContext.Provider>
  ) : null
}

export default AuthContext
